import { ROOT_CONTEXT, SpanKind, SpanStatusCode, trace, type Context, type Histogram, type Meter, type Tracer } from "@opentelemetry/api";
import { telemetryRoute } from "./telemetry-policy.ts";

type Runtime = { tracer: Tracer; meter: Meter; requests: Histogram; flush: () => Promise<unknown> };
let runtime: Runtime | undefined;
let starting = false;
let attempted = false;
let bearer = "";
let pageContext: Context = ROOT_CONTEXT;
let pageRoute = "/";

/** Called only after normal sign-in has acquired a token. Telemetry itself
 * never triggers interactive authentication, and has no ingestion credential. */
export function configureTelemetry(accessToken: string, base: string): void {
  if (typeof window === "undefined" || new URL(base || "/", window.location.origin).origin !== window.location.origin) return;
  bearer = accessToken;
  if (starting || attempted) return;
  starting = true;
  void fetch("/api/telemetry/config", { headers: { Authorization: `Bearer ${bearer}` }, cache: "no-store" })
    .then(async (response) => {
      if (!response.ok) return;
      const config = await response.json() as { enabled?: boolean; sample_ratio: number; environment: string; version: string };
      attempted = true;
      if (config.enabled !== true) return;
      const [{ WebTracerProvider }, { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler }, { resourceFromAttributes }, { W3CTraceContextPropagator }, { OTLPTraceExporter }, { MeterProvider, PeriodicExportingMetricReader }, { OTLPMetricExporter }, vitals] = await Promise.all([
        import("@opentelemetry/sdk-trace-web"), import("@opentelemetry/sdk-trace-base"),
        import("@opentelemetry/resources"), import("@opentelemetry/core"),
        import("@opentelemetry/exporter-trace-otlp-proto"), import("@opentelemetry/sdk-metrics"),
        import("@opentelemetry/exporter-metrics-otlp-proto"), import("web-vitals"),
      ]);
      const resource = resourceFromAttributes({ "service.name": "jml-browser", "service.namespace": "jml", "service.instance.id": crypto.randomUUID(), "service.version": config.version, "deployment.environment.name": config.environment });
      const headers = async () => ({ Authorization: `Bearer ${bearer}` });
      const provider = new WebTracerProvider({ resource, sampler: new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(config.sample_ratio) }), spanProcessors: [new BatchSpanProcessor(
        new OTLPTraceExporter({ url: "/api/telemetry/traces", headers, timeoutMillis: 5000 }),
        { maxQueueSize: 256, maxExportBatchSize: 64, scheduledDelayMillis: 10000, exportTimeoutMillis: 5000 },
      )] });
      provider.register({ propagator: new W3CTraceContextPropagator() });
      const meters = new MeterProvider({ resource, readers: [new PeriodicExportingMetricReader({
        exporter: new OTLPMetricExporter({ url: "/api/telemetry/metrics", headers, timeoutMillis: 5000 }),
        exportIntervalMillis: 60000, exportTimeoutMillis: 5000,
      })] });
      const meter = meters.getMeter("jml.browser");
      runtime = { tracer: provider.getTracer("jml.browser"), meter,
        requests: meter.createHistogram("http.client.request.duration", { unit: "s" }),
        flush: () => Promise.allSettled([provider.forceFlush(), meters.forceFlush()]),
      };
      navigation(pageRoute);
      const reportVital = (metric: { name: string; value: number }) => {
        const seconds = metric.name !== "CLS";
        meter.createHistogram(`browser.web_vital.${metric.name.toLowerCase()}`, { unit: seconds ? "s" : "1" })
          .record(seconds ? metric.value / 1000 : metric.value);
      };
      vitals.onCLS(reportVital); vitals.onINP(reportVital); vitals.onLCP(reportVital);
      const navigationTiming = performance.getEntriesByType("navigation")[0];
      if (navigationTiming) meter.createHistogram("browser.page.load.duration", { unit: "s" }).record(navigationTiming.duration / 1000);
      window.addEventListener("error", () => browserError("uncaught"));
      window.addEventListener("unhandledrejection", () => browserError("unhandled_rejection"));
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") void runtime?.flush();
      });
    }).catch(() => { /* Observability must never break sign-in or a business request. */ })
    .finally(() => { starting = false; });
}

export function navigation(path: string): void {
  pageRoute = telemetryRoute(path);
  if (!runtime) return;
  const span = runtime.tracer.startSpan("browser.navigation", { attributes: { "http.route": pageRoute } }, ROOT_CONTEXT);
  pageContext = trace.setSpan(ROOT_CONTEXT, span);
  runtime.meter.createCounter("browser.navigation.count").add(1, { "http.route": pageRoute });
  span.end();
}

export function browserError(kind: "uncaught" | "unhandled_rejection" | "react"): void {
  if (!runtime) return;
  const span = runtime.tracer.startSpan("browser.error", { attributes: { "error.type": kind, "http.route": pageRoute } }, pageContext);
  span.setStatus({ code: SpanStatusCode.ERROR, message: "browser error" });
  runtime.meter.createCounter("browser.error.count").add(1, { "error.type": kind });
  // Exception messages, stacks, DOM content and console arguments may contain PII.
  span.end();
}

export function apiObservation(path: string, method: string): { headers: Record<string, string>; finish: (status: number, cancelled?: boolean) => void } {
  if (!runtime) return { headers: {}, finish: () => {} };
  const route = telemetryRoute(path);
  const attributes = { "http.request.method": method, "http.route": route };
  const span = runtime.tracer.startSpan(`${method} ${route}`, { kind: SpanKind.CLIENT, attributes }, pageContext);
  const context = span.spanContext();
  const headers = { traceparent: `00-${context.traceId}-${context.spanId}-${context.traceFlags.toString(16).padStart(2, "0")}` };
  const start = performance.now();
  const meter = runtime.requests;
  return { headers, finish: (status, cancelled = false) => {
    span.setAttribute("http.response.status_code", status);
    if (cancelled) span.setAttribute("jml.cancelled", true);
    else if (status === 0 || status >= 400) span.setStatus({ code: SpanStatusCode.ERROR, message: "API request failed" });
    meter.record((performance.now() - start) / 1000, { ...attributes, "http.response.status_code": status, "jml.cancelled": cancelled });
    span.end();
  } };
}
